import { useEffect, useRef, useState } from "react";
import { Save, RotateCcw, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Field } from "./kit";

function TagChips({ tags, onPick }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="invite-tag-chips">
      <span className="text-xs muted mr-1 self-center">Insert:</span>
      {tags.map((t) => <button key={t} type="button" onClick={() => onPick(`{${t}}`)} className="chip py-1 px-2 text-xs bg-slate-100 hover:bg-[#FFF1EE] hover:text-[#FF8166]" data-testid={`invite-tag-${t}`}>{`{${t}}`}</button>)}
    </div>
  );
}

export function EmailPreview({ payload, ready }) {
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const key = JSON.stringify(payload);
  useEffect(() => {
    if (!ready) return;
    const t = setTimeout(() => api.post("/admin/invites/preview", payload).then((r) => { setP(r.data); setErr(""); }).catch((e) => setErr(errMsg(e))), 450);
    return () => clearTimeout(t);
  }, [key, ready]); // eslint-disable-line
  return (
    <div className="rounded-2xl border border-slate-200 overflow-hidden bg-slate-50 flex flex-col" data-testid="invite-email-preview">
      <div className="px-4 py-2 border-b border-slate-200 bg-white text-xs"><span className="muted">Subject: </span><b data-testid="invite-preview-subject">{p?.subject || "…"}</b></div>
      {err ? <p className="p-4 text-sm text-rose-700" data-testid="invite-preview-error">{err}</p>
        : <iframe title="Invite email preview" srcDoc={p?.html || ""} sandbox="allow-same-origin" className="w-full h-[460px] bg-[#FAF9F6]" data-testid="invite-preview-frame" />}
    </div>
  );
}

export function InviteEmailEditor({ tpl, setTpl, offer, rows, meta }) {
  const msgRef = useRef(null);
  const [busy, setBusy] = useState("");
  const payload = { ...offer, months: +offer.months, template: tpl, rows: rows.slice(0, 5) };
  const insert = (tag) => {
    const el = msgRef.current;
    const at = el ? el.selectionStart : tpl.message.length;
    setTpl({ ...tpl, message: tpl.message.slice(0, at) + tag + tpl.message.slice(at) });
    setTimeout(() => { el?.focus(); el?.setSelectionRange(at + tag.length, at + tag.length); }, 0);
  };
  const run = async (name, fn) => { setBusy(name); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); } };
  const saveDefault = () => run("save", async () => { await api.put("/admin/invites/template", tpl); toast.success("Saved as your default invite email"); });
  const sendTest = () => run("test", async () => { const { data } = await api.post("/admin/invites/test", payload); toast.success(`Test email sent to ${data.to}`); });
  return (
    <div className="grid lg:grid-cols-2 gap-5 mt-5" data-testid="invite-email-editor">
      <div className="space-y-3">
        <h3 className="font-bold text-sm">Invite email</h3>
        <Field label="Subject"><input className="input" value={tpl.subject} maxLength={150} onChange={(e) => setTpl({ ...tpl, subject: e.target.value })} data-testid="invite-tpl-subject" /></Field>
        <Field label="Message" hint="Leave a blank line between paragraphs. Tags are filled in for each provider.">
          <textarea ref={msgRef} className="input min-h-[220px] text-sm leading-relaxed" value={tpl.message} maxLength={3000} onChange={(e) => setTpl({ ...tpl, message: e.target.value })} data-testid="invite-tpl-message" />
        </Field>
        <TagChips tags={meta.tags} onPick={insert} />
        <Field label="Button text"><input className="input" value={tpl.button} maxLength={40} onChange={(e) => setTpl({ ...tpl, button: e.target.value })} data-testid="invite-tpl-button" /></Field>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={sendTest} disabled={!!busy} className="btn-navy btn-sm" data-testid="invite-test-btn"><MailCheck size={14} />{busy === "test" ? "Sending…" : "Send test to me"}</button>
          <button type="button" onClick={saveDefault} disabled={!!busy} className="btn-ghost btn-sm" data-testid="invite-save-default-btn"><Save size={14} />Save as my default</button>
          <button type="button" onClick={() => setTpl(meta.default)} className="btn-ghost btn-sm" data-testid="invite-reset-tpl-btn"><RotateCcw size={14} />Original wording</button>
        </div>
        <p className="text-xs muted">The test email uses {rows.length ? "the first provider in your list" : "a sample provider"} and a sample link.</p>
      </div>
      <EmailPreview payload={payload} ready={!!offer.plan} />
    </div>
  );
}
